import { API_BASE_URL } from "@/lib/constants/api";

/**
 * Server-side revocation of the caller's own refresh token, on sign-out.
 *
 * Deliberately not the shared axios client. Two reasons, both of which made the
 * previous attempt a no-op rather than a slow path:
 *
 * 1. The client's request interceptor reads the access token out of
 *    `authStore`, and sign-out clears that store synchronously — before a
 *    dynamically imported client could send anything. The request went out with
 *    no `Authorization` header, `/auth/logout/` answered 401, and the swallowed
 *    error hid it. The token stayed valid for the rest of its seven days and
 *    the session stayed listed under Active sessions, so every sign-in stacked
 *    another entry. The token is passed in here instead of being looked up.
 * 2. Sign-out is usually followed by `window.location.href = "/login"`, which
 *    cancels requests in flight. `keepalive` is the browser's guarantee that a
 *    small request survives the page going away.
 *
 * `authStore` cannot import the axios client anyway — the client imports the
 * store. The base URL carries none of that cycle, so it is shared from
 * `lib/constants/api` rather than repeated here, where the two copies could
 * drift apart on a deploy change.
 */

/**
 * Ask the server to blacklist `refreshToken`. Fire-and-forget by design: the
 * local session is being torn down either way, and a failure here must not
 * leave the reader looking at a page they think they have left.
 */
export function revokeRefreshToken(
  accessToken: string,
  refreshToken: string,
): void {
  try {
    void fetch(`${API_BASE_URL}/auth/logout/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ refresh: refreshToken }),
      keepalive: true,
    }).catch(() => {
      // Nothing to retry into: the tokens are gone from this tab by now.
    });
  } catch {
    // `fetch` itself is unavailable (very old browser, or a test environment
    // without it). Sign-out must still complete locally.
  }
}
